/**
 * QuickBooks Connection Status Component
 *
 * Displays QuickBooks connection status and provides connect/disconnect functionality.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle as ConnectedIcon,
  Error as DisconnectedIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
} from '@mui/icons-material';
import {
  useGetConnectionStatusQuery,
  useInitiateConnectionMutation,
  useDisconnectQuickBooksMutation,
} from '../../../api/quickbooksApi';
import { useAppDispatch } from '../../../store/hooks';
import { showSuccessNotification } from '../../notifications';

interface ConnectionStatusProps {
  studioId: string;
}

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({ studioId }) => {
  const dispatch = useAppDispatch();
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  // Queries and mutations
  const {
    data: connectionStatus,
    isLoading,
    refetch,
  } = useGetConnectionStatusQuery(studioId);
  const [initiateConnection, { isLoading: isConnecting }] =
    useInitiateConnectionMutation();
  const [disconnectQuickBooks, { isLoading: isDisconnecting }] =
    useDisconnectQuickBooksMutation();

  const handleConnect = async () => {
    try {
      const result = await initiateConnection(studioId).unwrap();

      // Open QuickBooks OAuth in popup
      const popup = window.open(
        result.authorization_url,
        'quickbooks-auth',
        'width=800,height=600'
      );

      if (!popup) {
        setError('Please allow popups to connect QuickBooks');
        return;
      }

      setError(null);
      setIsPolling(true);

      console.log('Polling for OAuth completion...');

      // Poll connection status every 2 seconds
      // (Can't use localStorage because popup is on different origin)
      const pollInterval = setInterval(async () => {
        console.log('Checking connection status...');

        const statusResult = await refetch();

        if (statusResult.data?.is_connected) {
          console.log('QuickBooks connected successfully!');

          // Clear polling interval
          clearInterval(pollInterval);

          setIsPolling(false);

          // Show success notification
          dispatch(
            showSuccessNotification({
              message: 'QuickBooks connected successfully!',
              successType: 'general',
            })
          );
        }
      }, 2000); // Check every 2 seconds

      // Cleanup: stop polling after 5 minutes (safety timeout)
      setTimeout(() => {
        clearInterval(pollInterval);
        setIsPolling(false);
      }, 300000);
    } catch (err: any) {
      setError(
        err?.data?.error || 'Failed to connect QuickBooks. Please try again.'
      );
    }
  };

  const handleDisconnectClick = () => {
    setShowDisconnectDialog(true);
  };

  const handleDisconnectCancel = () => {
    setShowDisconnectDialog(false);
  };

  const handleDisconnectConfirm = async () => {
    setShowDisconnectDialog(false);

    try {
      await disconnectQuickBooks(studioId).unwrap();
      dispatch(
        showSuccessNotification({
          message: 'QuickBooks disconnected successfully',
          successType: 'general',
        })
      );
      setError(null);
    } catch (err: any) {
      setError(
        err?.data?.error || 'Failed to disconnect QuickBooks. Please try again.'
      );
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress size={24} />
          <Typography sx={{ ml: 2 }}>
            Checking QuickBooks connection...
          </Typography>
        </Box>
      </Paper>
    );
  }

  const isConnected = connectionStatus?.is_connected || false;

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        {/* Connection Status */}
        <Box display="flex" alignItems="center" gap={2}>
          {isConnected ? (
            <>
              <ConnectedIcon color="success" fontSize="large" />
              <Box>
                <Typography variant="h6" gutterBottom>
                  QuickBooks Connected
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {connectionStatus?.environment && (
                    <Chip
                      label={connectionStatus.environment.toUpperCase()}
                      size="small"
                      color={
                        connectionStatus.environment === 'production'
                          ? 'primary'
                          : 'warning'
                      }
                    />
                  )}
                  {connectionStatus?.company_name &&
                  connectionStatus?.realm_id ? (
                    <>
                      <Chip
                        label={connectionStatus.company_name}
                        size="small"
                        color="primary"
                      />
                      <Chip
                        label={`ID: ${connectionStatus.realm_id.slice(0, 8)}...`}
                        size="small"
                        variant="outlined"
                      />
                    </>
                  ) : (
                    connectionStatus?.realm_id && (
                      <Chip
                        label={`Company: ${connectionStatus.realm_id}`}
                        size="small"
                        variant="outlined"
                      />
                    )
                  )}
                </Box>
                {connectionStatus?.last_sync && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Last synced:{' '}
                    {new Date(connectionStatus.last_sync).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <>
              <DisconnectedIcon color="error" fontSize="large" />
              <Box>
                <Typography variant="h6" gutterBottom>
                  QuickBooks Not Connected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect your QuickBooks account to upload sales receipts
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Action Buttons */}
        <Box>
          {isConnected ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={<UnlinkIcon />}
              onClick={handleDisconnectClick}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<LinkIcon />}
              onClick={handleConnect}
              disabled={isConnecting || isPolling}
            >
              {(() => {
                if (isConnecting) return 'Connecting...';
                if (isPolling) return 'Waiting for authorization...';
                return 'Connect QuickBooks';
              })()}
            </Button>
          )}
        </Box>
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Warning for token expiration */}
      {connectionStatus?.needs_refresh && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Your QuickBooks connection needs to be refreshed. The system will
          automatically refresh it on the next upload.
        </Alert>
      )}

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onClose={handleDisconnectCancel}
        aria-labelledby="disconnect-dialog-title"
        aria-describedby="disconnect-dialog-description"
      >
        <DialogTitle id="disconnect-dialog-title">
          Disconnect QuickBooks?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="disconnect-dialog-description">
            Are you sure you want to disconnect QuickBooks? This will remove all
            connection settings and you&apos;ll need to reconnect to upload
            sales receipts.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisconnectCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDisconnectConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ConnectionStatus;
